import BaseModel from '../baseModel';
import CPromise from '../cPromise';
import genGuid from '../guid';
import { isPromise } from '../isPromise';
import { iterableBuffer, IterableBufferProducer } from '../iterableBuffer';
import Bus, { type BusOptions } from './bus';
import type { BusSubscribeOptions } from './bus_subscribe';
import type { StandardSchemaV1 } from '@standard-schema/spec';
import { deepEqual } from 'cross-state';

export interface BusRequestOptions extends Partial<BusOptions>, BusSubscribeOptions {
    baseModel?: boolean;
}

export type BusRequestResponse<T, TOptions extends BusRequestOptions> = TOptions extends { baseModel: true } ? BaseModel<T> : T;

export interface BusRequestResult<T, TOptions extends BusRequestOptions> extends CPromise<BusRequestResponse<T, TOptions>> {
    guid: string;
    correlationGuid: string;
}

export function bus_request<T, TOptions extends BusRequestOptions = BusRequestOptions>(
    this: Bus,
    topic: string | [string, string],
    data?: unknown,
    options?: TOptions,
): BusRequestResult<T, TOptions>;

export function bus_request<T, TOptions extends BusRequestOptions = BusRequestOptions>(
    this: Bus,
    topic: string | [string, string],
    data: unknown,
    responseSchema: StandardSchemaV1<unknown, T>,
    options?: TOptions,
): BusRequestResult<T, TOptions>;

export function bus_request<T, TOptions extends BusRequestOptions = BusRequestOptions>(
    this: Bus,
    topic: string | [string, string],
    data?: unknown,
    ...args: [TOptions?] | [StandardSchemaV1<unknown, T>, TOptions?]
): BusRequestResult<T, TOptions> {
    let responseSchema: StandardSchemaV1<unknown, T> | undefined;
    let options: TOptions | undefined;

    if (args[0] && '~standard' in args[0]) {
        [responseSchema, options] = args as [StandardSchemaV1<unknown, T>, TOptions?];
    } else {
        [options] = args as [TOptions?];
    }

    const {
        backoff = this.options.backoff,
        maxDelay = this.options.maxDelay,
        debug = this.options.debug,
        baseModel: returnBaseModel = false,
        signal,
        measureTime = true,
    } = options ?? {};

    let { timeout = this.options.timeout, retry = this.options.retry, delay = this.options.delay ?? 0, correlationGuid } = options ?? {};

    let guid = genGuid();
    correlationGuid ??= guid;
    timeout = Math.max(timeout, 0);

    const requestTopic = typeof topic === 'string' ? `${topic}.request` : topic[0];
    const responseTopic = typeof topic === 'string' ? `${topic}.response` : topic[1];

    const request = new CPromise<T | AsyncIterable<T> | BaseModel<T>>(async (resolve, reject, onCancel) => {
        let timeoutId: NodeJS.Timeout | undefined;
        let producer: IterableBufferProducer<any> | undefined;

        const cancelListener = this.subscribe<T>(
            responseTopic,
            async (data, _topic, pkg) => {
                if (timeoutId !== undefined) {
                    clearTimeout(timeoutId);
                }

                if (pkg?.error) {
                    if (producer) producer.throw(Error(pkg.error));
                    else reject(pkg.error);
                    cancelListener();
                } else if (pkg?.chunk !== undefined) {
                    const chunk = pkg.chunk;

                    if (!producer) {
                        let consumer;
                        [producer, consumer] = iterableBuffer<T>();
                        resolve(consumer);

                        if (signal) {
                            signal.addEventListener('abort', () => {
                                producer?.throw(new Error('Request was canceled.'));
                                cancelListener();
                            });
                        }
                    }

                    if (returnBaseModel || !deepEqual(pkg.data ?? {}, {})) {
                        producer.push(returnBaseModel ? pkg : data);
                    }

                    if (pkg.isLastChunk) {
                        producer.complete();
                        cancelListener();
                    } else if (timeout) {
                        timeoutId = setTimeout(() => {
                            cancelListener();
                            producer?.throw(Error(`Chunk ${chunk + 1} timed out.`));
                        }, timeout);
                    }
                } else {
                    cancelListener();

                    if (responseSchema) {
                        let result = responseSchema['~standard'].validate(data);

                        if (isPromise(result)) {
                            result = await result;
                        }

                        if (result.issues) {
                            reject(
                                Object.assign(new Error(`Validation failed for topic ${topic}`), {
                                    data,
                                    issues: result.issues,
                                    get baseModel() {
                                        return pkg;
                                    },
                                }),
                            );
                            return;
                        }

                        data = result.value;
                        pkg.data = result.value;
                    }

                    resolve(returnBaseModel ? pkg : data);
                }
            },
            { correlationGuid, debug, measureTime },
        );

        const send = () => {
            this.publish(requestTopic, data ?? {}, { guid, correlationGuid, debug });
            if (timeout) {
                timeoutId = setTimeout(() => {
                    if (retry !== undefined && retry--) {
                        guid = genGuid();
                        timeoutId = setTimeout(send, delay);
                        if (backoff) {
                            delay *= 2;
                        }
                        if (maxDelay) {
                            delay = Math.min(delay, maxDelay);
                        }
                    } else {
                        cancelListener();
                        reject(Object.assign(new Error(`${responseTopic} timed out after ${timeout}ms`), { isTimeout: true }));
                        debug?.(`${responseTopic} timed out after ${timeout}ms`);
                    }
                }, timeout);
            }
        };
        send();

        const cancel = () => {
            cancelListener();
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }

            reject(new Error('Request was canceled.'));
        };

        onCancel(cancel);
    });

    signal?.addEventListener('abort', () => request.cancel());

    return Object.assign(request, { guid, correlationGuid }) as BusRequestResult<T, TOptions>;
}
